/**
 * MONSTER TOWN — FOOD PICTURES. COOKING CREW (src/cooking/**).
 *
 * LAW #4 lives here for the kitchen and the zoo. Every ingredient, every dish
 * and every animal a kid ever sees on a card is one of these, and there is no
 * text path anywhere behind them.
 *
 * Drawn in the same hand as `src/ui/icons.ts` on purpose — one viewBox
 * (0 0 100 100), one ink colour, stroke 7 for silhouette and 5 for detail,
 * round caps, fills from `palette.ts` and no raw hex. They sit next to the HUD's
 * own icons on the same card and must not read as a second art set.
 *
 * `src/ui/icons.ts` belongs to the HUD crew and this expansion does not touch
 * it: these are additive, in this crew's folder, and reachable through the same
 * shaped helper (`picEl`) so a caller cannot tell the difference.
 */

import { css, type PaletteName } from '../core/palette'
import type { FoodPic } from './types'

const INK = css('ink')
const S = `stroke="${INK}" stroke-linecap="round" stroke-linejoin="round"`
const OUT = `${S} stroke-width="7"`
const DET = `${S} stroke-width="5"`

export interface PicPen {
  color: PaletteName
  accent: PaletteName
}

// ---------------------------------------------------------------------------
// ingredients
// ---------------------------------------------------------------------------

const FOOD: Record<FoodPic, (p: PicPen) => string> = {
  /** An apple-shaped fruit with a leaf. The reference errand's whole supply. */
  fruit: (p) => `
    <path d="M50 30 C30 30 20 46 24 62 C28 79 41 88 50 88 C59 88 72 79 76 62 C80 46 70 30 50 30 Z"
      fill="${css(p.color)}" ${OUT}/>
    <path d="M50 30 L50 16" ${OUT} fill="none"/>
    <path d="M50 20 C60 8 76 10 78 18 C80 27 62 30 50 20 Z" fill="${css(p.accent)}" ${DET}/>
    <path d="M36 50 C34 58 36 66 41 72" stroke="${css('flowerWhite')}" stroke-width="5"
      stroke-linecap="round" fill="none" opacity="0.75"/>`,

  /** Three berries on a sprig. */
  berry: (p) => `
    <path d="M50 24 L50 40" ${DET} fill="none"/>
    <circle cx="34" cy="58" r="19" fill="${css(p.color)}" ${OUT}/>
    <circle cx="66" cy="58" r="19" fill="${css(p.color)}" ${OUT}/>
    <circle cx="50" cy="76" r="17" fill="${css(p.accent)}" ${OUT}/>
    <circle cx="28" cy="52" r="4" fill="${css('flowerWhite')}"/>
    <circle cx="60" cy="52" r="4" fill="${css('flowerWhite')}"/>`,

  /** A carrot: the one vegetable a three-year-old can name from a silhouette. */
  veg: (p) => `
    <path d="M50 88 L30 44 C40 36 60 36 70 44 Z" fill="${css(p.color)}" ${OUT}/>
    <path d="M40 56 L52 60 M46 70 L58 72" stroke="${css('flowerWhite')}" stroke-width="4"
      stroke-linecap="round" opacity="0.7"/>
    <path d="M50 42 C46 24 34 18 26 20 C30 34 40 40 50 42 Z" fill="${css(p.accent)}" ${DET}/>
    <path d="M52 42 C58 24 70 20 78 22 C74 36 62 42 52 42 Z" fill="${css(p.accent)}" ${DET}/>`,

  /** A sheaf of grain. */
  grain: (p) => `
    <path d="M50 90 L50 40" ${OUT} fill="none"/>
    <path d="M50 44 C34 42 26 32 28 22 C40 22 48 30 50 44 Z" fill="${css(p.color)}" ${DET}/>
    <path d="M50 44 C66 42 74 32 72 22 C60 22 52 30 50 44 Z" fill="${css(p.color)}" ${DET}/>
    <path d="M50 62 C36 60 28 52 30 42 C42 42 48 50 50 62 Z" fill="${css(p.accent)}" ${DET}/>
    <path d="M50 62 C64 60 72 52 70 42 C58 42 52 50 50 62 Z" fill="${css(p.accent)}" ${DET}/>`,

  /** A milk bottle. */
  milk: (p) => `
    <path d="M40 18 L60 18 L60 32 L70 46 L70 84 C70 88 67 90 64 90 L36 90
      C33 90 30 88 30 84 L30 46 L40 32 Z" fill="${css(p.color)}" ${OUT}/>
    <path d="M32 60 L68 60 L68 84 C68 87 66 88 64 88 L36 88 C34 88 32 87 32 84 Z"
      fill="${css(p.accent)}" ${DET}/>
    <path d="M38 14 L62 14" ${OUT} fill="none"/>`,

  /** A honey pot with a dipper. */
  honey: (p) => `
    <path d="M26 46 L74 46 L70 86 C70 89 67 90 64 90 L36 90 C33 90 30 89 30 86 Z"
      fill="${css(p.color)}" ${OUT}/>
    <path d="M22 40 L78 40 L78 50 L22 50 Z" fill="${css(p.accent)}" ${OUT}/>
    <path d="M50 40 L50 12" ${DET} fill="none"/>
    <circle cx="50" cy="12" r="8" fill="${css(p.accent)}" ${DET}/>
    <path d="M40 62 C46 70 54 70 60 62" stroke="${css('flowerWhite')}" stroke-width="5"
      stroke-linecap="round" fill="none" opacity="0.8"/>`,

  // -- dishes --------------------------------------------------------------

  /** THE FRUIT BOWL. The picture the whole reference errand points at. */
  bowl: (p) => `
    <circle cx="34" cy="42" r="14" fill="${css(p.accent)}" ${DET}/>
    <circle cx="52" cy="34" r="15" fill="${css(p.color)}" ${DET}/>
    <circle cx="70" cy="44" r="13" fill="${css(p.accent)}" ${DET}/>
    <path d="M14 52 L86 52 C86 74 70 88 50 88 C30 88 14 74 14 52 Z"
      fill="${css('plate')}" ${OUT}/>
    <path d="M24 62 C30 74 40 80 50 81" stroke="${css('glassDark')}" stroke-width="5"
      stroke-linecap="round" fill="none" opacity="0.7"/>`,

  /** A steaming bowl of stew. */
  stew: (p) => `
    <path d="M30 20 C30 12 38 12 38 20 M50 16 C50 8 58 8 58 16 M68 20 C68 12 76 12 76 20"
      stroke="${css('glassDark')}" stroke-width="5" stroke-linecap="round" fill="none"/>
    <path d="M14 44 L86 44 C86 70 70 84 50 84 C30 84 14 70 14 44 Z"
      fill="${css(p.color)}" ${OUT}/>
    <path d="M14 44 L86 44" ${OUT} fill="none"/>
    <circle cx="36" cy="38" r="7" fill="${css(p.accent)}" ${DET}/>
    <circle cx="58" cy="36" r="8" fill="${css(p.accent)}" ${DET}/>
    <path d="M10 84 L90 84" ${OUT} fill="none"/>`,

  /** A slice of cake with a cherry. */
  cake: (p) => `
    <path d="M22 84 L22 46 L78 46 L78 84 Z" fill="${css(p.color)}" ${OUT}/>
    <path d="M18 46 C18 34 30 28 50 28 C70 28 82 34 82 46 C74 52 62 44 50 50
      C38 56 26 50 18 46 Z" fill="${css(p.accent)}" ${OUT}/>
    <circle cx="50" cy="20" r="9" fill="${css('cherry')}" ${DET}/>
    <path d="M22 66 L78 66" stroke="${css('flowerWhite')}" stroke-width="5" opacity="0.6"/>`,

  /** A stack of pancakes with a pat of butter. */
  pancake: (p) => `
    <ellipse cx="50" cy="76" rx="34" ry="12" fill="${css(p.color)}" ${OUT}/>
    <ellipse cx="50" cy="60" rx="32" ry="12" fill="${css(p.color)}" ${OUT}/>
    <ellipse cx="50" cy="44" rx="30" ry="12" fill="${css(p.color)}" ${OUT}/>
    <path d="M24 42 C34 52 40 40 50 46 C60 52 68 40 76 46" stroke="${css(p.accent)}"
      stroke-width="7" stroke-linecap="round" fill="none"/>
    <rect x="42" y="24" width="18" height="12" rx="3" fill="${css('flowerYellow')}" ${DET}/>`,

  /** A jam jar with a cloth lid. */
  jam: (p) => `
    <path d="M28 40 L72 40 L72 86 C72 89 70 90 68 90 L32 90 C30 90 28 89 28 86 Z"
      fill="${css(p.color)}" ${OUT}/>
    <path d="M24 26 C24 20 76 20 76 26 L76 38 L24 38 Z" fill="${css(p.accent)}" ${OUT}/>
    <circle cx="42" cy="60" r="6" fill="${css('flowerWhite')}" opacity="0.75"/>
    <circle cx="58" cy="72" r="5" fill="${css('flowerWhite')}" opacity="0.6"/>`,

  /** A tall glass of juice with a straw. */
  juice: (p) => `
    <path d="M32 26 L68 26 L62 88 C62 90 60 90 58 90 L42 90 C40 90 38 90 38 88 Z"
      fill="${css(p.color)}" ${OUT}/>
    <path d="M30 44 L70 44" ${DET} fill="none"/>
    <path d="M58 26 L74 6" stroke="${css(p.accent)}" stroke-width="9" stroke-linecap="round"/>
    <circle cx="46" cy="60" r="5" fill="${css('flowerWhite')}" opacity="0.7"/>`,
}

/** Raw SVG markup for a food. Unknown pictures fall back to a friendly bowl. */
export function foodSvg(pic: FoodPic, pen: PicPen): string {
  const draw = FOOD[pic] ?? FOOD.bowl
  return `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true">${draw(pen)}</svg>`
}

// ---------------------------------------------------------------------------
// WHERE A THING COMES FROM — the answer to "I have not got any"
// ---------------------------------------------------------------------------
/*
 * ROUND 2. A kid critic tapped the only tile on a cook screen with an empty bag
 * and reported that the game answered with a NOISE. It did in fact wobble the
 * tile and pin a 36 px badge to its corner — but the tile was at 50 % opacity,
 * the badge was a generic line icon at the corner of a faded control, and 900 ms
 * later a `repaint()` deliberately threw the whole answer away. On a muted iPad
 * with a screen already looked at, that is indistinguishable from nothing.
 *
 * These are the pictures behind the fix. They are places, not symbols: the FRUIT
 * TREE in the kid's own garden, the vegetable bed, the berry bush, the pantry in
 * this very room, the shop, a present. A three-year-old who has walked past the
 * tree recognises it; nobody has to read the word "garden".
 *
 * Same hand as the food above — one viewBox, one ink, stroke 7/5, palette fills.
 */

export type SourcePic = 'tree' | 'patch' | 'bush' | 'pantry' | 'shop' | 'gift'

const SOURCE: Record<SourcePic, string> = {
  /** The fruit tree in the garden. The reference errand's whole supply. */
  tree: `
    <rect x="42" y="52" width="16" height="42" rx="6" fill="${css('trunk')}" ${OUT}/>
    <circle cx="28" cy="50" r="19" fill="${css('leafLight')}" ${OUT}/>
    <circle cx="72" cy="50" r="19" fill="${css('leafDark')}" ${OUT}/>
    <circle cx="50" cy="34" r="26" fill="${css('leaf')}" ${OUT}/>
    <circle cx="36" cy="32" r="8" fill="${css('flowerRed')}" ${DET}/>
    <circle cx="64" cy="42" r="8" fill="${css('flowerRed')}" ${DET}/>
    <circle cx="54" cy="18" r="7" fill="${css('flowerRed')}" ${DET}/>`,

  /** The raised vegetable bed. */
  patch: `
    <path d="M28 62 V40 M50 62 V32 M72 62 V40" ${DET} fill="none"/>
    <circle cx="28" cy="36" r="13" fill="${css('leafLight')}" ${OUT}/>
    <circle cx="72" cy="36" r="13" fill="${css('leafLight')}" ${OUT}/>
    <circle cx="50" cy="28" r="15" fill="${css('leaf')}" ${OUT}/>
    <path d="M12 62 H88 L82 90 H18 Z" fill="${css('dirtDark')}" ${OUT}/>
    <rect x="8" y="56" width="84" height="14" rx="6" fill="${css('woodLight')}" ${OUT}/>`,

  /** The berry bush. */
  bush: `
    <path d="M16 84 H84" ${OUT} fill="none"/>
    <circle cx="32" cy="54" r="23" fill="${css('leafDark')}" ${OUT}/>
    <circle cx="68" cy="54" r="23" fill="${css('leafDark')}" ${OUT}/>
    <circle cx="50" cy="36" r="23" fill="${css('leaf')}" ${OUT}/>
    <circle cx="34" cy="44" r="8" fill="${css('flowerPurple')}" ${DET}/>
    <circle cx="66" cy="40" r="8" fill="${css('flowerPurple')}" ${DET}/>
    <circle cx="50" cy="62" r="8" fill="${css('flowerPurple')}" ${DET}/>`,

  /** The store cupboard standing in this very room. */
  pantry: `
    <rect x="24" y="4" width="20" height="18" rx="5" fill="${css('roofMustard')}" ${DET}/>
    <rect x="54" y="2" width="20" height="20" rx="5" fill="${css('flowerPurple')}" ${DET}/>
    <rect x="10" y="20" width="80" height="14" rx="6" fill="${css('woodDark')}" ${OUT}/>
    <rect x="18" y="32" width="64" height="60" rx="9" fill="${css('wallCream')}" ${OUT}/>
    <path d="M50 36 V88" ${DET} fill="none"/>
    <circle cx="42" cy="62" r="5" fill="${INK}"/>
    <circle cx="58" cy="62" r="5" fill="${INK}"/>`,

  /** The town shop. */
  shop: `
    <path d="M22 38 H78 L84 90 C84 92 82 92 80 92 L20 92 C18 92 16 92 16 90 Z"
      fill="${css('uiPrimary')}" ${OUT}/>
    <path d="M36 40 V26 C36 14 64 14 64 26 V40" ${OUT} fill="none"/>
    <circle cx="50" cy="66" r="12" fill="${css('flowerWhite')}" ${DET}/>`,

  /** A present from a neighbour. */
  gift: `
    <rect x="18" y="42" width="64" height="48" rx="9" fill="${css('flowerPink')}" ${OUT}/>
    <rect x="10" y="30" width="80" height="16" rx="7" fill="${css('flowerRed')}" ${OUT}/>
    <path d="M50 30 V90" ${DET} fill="none"/>
    <path d="M50 30 C36 30 28 16 40 12 C50 9 51 22 50 30 Z" fill="${css('flowerRed')}" ${DET}/>
    <path d="M50 30 C64 30 72 16 60 12 C50 9 49 22 50 30 Z" fill="${css('flowerRed')}" ${DET}/>`,
}

/** Raw SVG body for a place. Unknown places fall back to the garden bed. */
export function sourceBody(kind: SourcePic): string {
  return SOURCE[kind] ?? SOURCE.patch
}

/**
 * THE ONE ARROW IN THE KITCHEN. The recipe card used to point with the HUD's
 * `back` chevron rotated 180 degrees at 70 % opacity — a thin grey line glyph,
 * and the one piece of default-looking UI on an otherwise painted screen. This
 * is the same arrow drawn the way everything else here is drawn: a chunky filled
 * shape in the palette with a full-weight ink outline.
 */
export function arrowBody(): string {
  return `<path d="M10 38 H48 V18 L90 50 L48 82 V62 H10 Z" fill="${css('uiPrimary')}" ${OUT}/>`
}

// ---------------------------------------------------------------------------
// THE POT, AND THE ROOM IT STANDS IN
// ---------------------------------------------------------------------------
/*
 * WHY THE POT IS DRAWN IN TWO HALVES.
 *
 * The kitchen used to put a faded picture of the FINISHED dish in the middle of
 * the screen at 34 % opacity, which is the lowest-contrast thing in a game made
 * of chunky saturated shapes — next to the world it reads as a disabled button,
 * and the dish a kid is working towards had no colour at all. Worse: it never
 * changed. One fruit in or none in looked identical, so the counting the whole
 * screen exists to teach was invisible on the biggest object on it.
 *
 * So the pot is now a real pot, in the palette's saturated teal, and the food a
 * kid has tapped in is drawn INSIDE it, in exactly the same picture they tapped
 * in the tray. That only works if the pot's near rim paints OVER the food, so
 * the art comes in a back half (far rim, inside, handles) and a front half (near
 * rim and belly) with the food sandwiched between them in the DOM. Three layers,
 * no clip paths, no masks — an older iPad pays nothing for it.
 */

const TEAL = css('roofTeal')
const TEAL_DARK = css('uiAccentDark')

/** Far rim, the dark inside, and both handles. Everything BEHIND the food. */
export function potBackSvg(): string {
  return `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true" width="100%" height="100%">
    <path d="M16 52 C2 54 2 74 18 73" fill="none" ${S} stroke-width="11"/>
    <path d="M84 52 C98 54 98 74 82 73" fill="none" ${S} stroke-width="11"/>
    <path d="M16 52 C4 54 4 72 18 71" fill="none" stroke="${TEAL}" stroke-width="5" stroke-linecap="round"/>
    <path d="M84 52 C96 54 96 72 82 71" fill="none" stroke="${TEAL}" stroke-width="5" stroke-linecap="round"/>
    <ellipse cx="50" cy="44" rx="34" ry="11" fill="${TEAL_DARK}" ${OUT}/>
  </svg>`
}

/** The near rim and the belly. Everything IN FRONT of the food. */
export function potFrontSvg(): string {
  return `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true" width="100%" height="100%">
    <path d="M16 44 C16 52 31 55 50 55 C69 55 84 52 84 44
             C84 75 72 89 50 89 C28 89 16 75 16 44 Z" fill="${TEAL}" ${OUT}/>
    <path d="M27 62 C25 73 31 82 40 86" stroke="${css('flowerWhite')}" stroke-width="6"
      stroke-linecap="round" fill="none" opacity="0.55"/>
    <path d="M18 60 H82" stroke="${TEAL_DARK}" stroke-width="5" stroke-linecap="round" opacity="0.55"/>
  </svg>`
}

/** Three wisps. Only ever drawn when there is something in the pot to steam. */
export function steamSvg(): string {
  return `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true" width="100%" height="100%">
    <g fill="none" stroke="${css('flowerWhite')}" stroke-width="7" stroke-linecap="round" opacity="0.95">
      <path class="mt-cook-wisp" d="M34 44 C26 32 40 26 32 12"/>
      <path class="mt-cook-wisp" d="M50 40 C42 26 58 20 50 4"/>
      <path class="mt-cook-wisp" d="M66 44 C58 32 72 26 64 12"/>
    </g>
  </svg>`
}

/** A wooden spoon, leaning in the pot. Appears the moment it is time to stir. */
export function spoonSvg(): string {
  return `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true" width="100%" height="100%">
    <path d="M62 58 L84 14" ${S} stroke-width="11" fill="none"/>
    <path d="M62 58 L84 14" stroke="${css('woodLight')}" stroke-width="6" stroke-linecap="round" fill="none"/>
    <ellipse cx="58" cy="64" rx="13" ry="10" transform="rotate(-24 58 64)" fill="${css('woodLight')}" ${OUT}/>
  </svg>`
}

/**
 * THE ROOM. Wall, tiles, a window with the day outside, a shelf of jars and a
 * pan on its rail — the reason this screen no longer looks like a settings page.
 *
 * Decoration only: `pointer-events: none`, one static SVG, no animation, and it
 * is outlined in `inkSoft` rather than `ink` so the things a finger can actually
 * land on stay the nearest, darkest shapes on the screen.
 *
 * ===========================================================================
 * ROUND 2 — IT IS THE SAME KITCHEN AS THE ONE A KID TAPPED THE POT IN
 * ===========================================================================
 * The critic, laying `04-the-kitchen-room.png` beside `07b-cooking-mid-close.png`:
 * *"a kid taps a drab room and gets a beautiful illustration; the two should be
 * recognisably the same kitchen."* The single biggest reason they did not read as
 * one room is that this drawing was the 3D room INVERTED — cream walls over a
 * mint floor, where the room a kid actually walks into is mint walls over a cream
 * tiled floor.
 *
 * That is not a taste call, it is a lookup. `src/interior/rooms.ts` opens the
 * kitchen on `wall: 1` and `floor: 1`, which are `WALL_STYLES[1]` "mint stripes"
 * (`wallpaperMint` over `wallMint`) and `FLOOR_STYLES[1]` "cream tiles"
 * (`floorTile` over `pathEdge`). Those four palette names are what this drawing
 * now uses, stripes and tile grid included. They are written out here rather than
 * imported because `src/interior/**` is another crew's module and crews never
 * import each other — `src/core/palette.ts` is shared and is where the colours
 * actually live, so the two rooms cannot drift apart in HUE, only in the index a
 * kid chooses with the paint can.
 *
 * THE TOP OF THE WALL IS DELIBERATELY BARE. The recipe card floats top-centre and
 * used to be pasted straight over the shelf's potted plant. Everything drawn here
 * now lives outside the column x 350–850, so the card lands on clear wall at
 * every recipe width the game ships (one step to three).
 */
export function kitchenRoomSvg(): string {
  const soft = css('inkSoft')
  const line = `stroke="${soft}" stroke-linecap="round" stroke-linejoin="round" fill="none"`
  // FLOOR_STYLES[1] "cream tiles": a 4-across grid in `pathEdge` on `floorTile`.
  const tiles: string[] = []
  for (let x = 0; x <= 1200; x += 150) {
    tiles.push(`<path d="M${x} 430 V760" stroke="${css('pathEdge')}" stroke-width="4" opacity="0.4" fill="none"/>`)
  }
  for (const y of [540, 650, 760]) {
    tiles.push(`<path d="M0 ${y} H1200" stroke="${css('pathEdge')}" stroke-width="4" opacity="0.4" fill="none"/>`)
  }
  // WALL_STYLES[1] "mint stripes": soft `wallMint` bands on `wallpaperMint`.
  const stripes: string[] = []
  for (let x = 24; x < 1200; x += 96) {
    stripes.push(`<rect x="${x}" y="0" width="32" height="430" fill="${css('wallMint')}" opacity="0.55"/>`)
  }
  return `<svg viewBox="0 0 1200 760" preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true" width="100%" height="100%">
    <rect x="0" y="0" width="1200" height="760" fill="${css('wallpaperMint')}"/>
    ${stripes.join('')}
    <rect x="0" y="430" width="1200" height="330" fill="${css('floorTile')}"/>
    ${tiles.join('')}
    <path d="M0 430 H1200" ${line} stroke-width="7" opacity="0.4"/>

    <rect x="872" y="70" width="296" height="242" rx="26" fill="${css('glass')}"/>
    <path d="M876 244 C924 200 972 214 1018 246 C1064 210 1118 220 1164 252 L1164 308 L876 308 Z"
      fill="${css('grassLight')}" opacity="0.9"/>
    <circle cx="1108" cy="126" r="30" fill="${css('flowerYellow')}" opacity="0.9"/>
    <rect x="872" y="70" width="296" height="242" rx="26" ${line} stroke-width="9"/>
    <path d="M1020 70 V312 M872 192 H1168" ${line} stroke-width="9"/>
    <rect x="852" y="302" width="336" height="30" rx="12" fill="${css('woodLight')}" ${line} stroke-width="8"/>

    <path d="M40 62 H330" ${line} stroke-width="8"/>
    <path d="M170 62 V78" ${line} stroke-width="8"/>
    <circle cx="170" cy="128" r="50" fill="${css('metal')}" ${line} stroke-width="9"/>
    <circle cx="170" cy="128" r="27" fill="${css('metalDark')}" opacity="0.45"/>
    <path d="M220 128 H326" ${line} stroke-width="14"/>

    <rect x="20" y="300" width="322" height="26" rx="11" fill="${css('woodLight')}" ${line} stroke-width="8"/>
    <path d="M62 326 V360 M300 326 V360" ${line} stroke-width="8"/>
    <rect x="52" y="246" width="70" height="56" rx="13" fill="${css('flowerYellow')}" ${line} stroke-width="7"/>
    <rect x="44" y="226" width="86" height="26" rx="10" fill="${css('roofMustard')}" ${line} stroke-width="7"/>
    <rect x="152" y="236" width="70" height="66" rx="13" fill="${css('flowerPurple')}" ${line} stroke-width="7"/>
    <rect x="144" y="216" width="86" height="26" rx="10" fill="${css('wallLilac')}" ${line} stroke-width="7"/>
    <path d="M254 302 L262 254 H322 L330 302 Z" fill="${css('roofRed')}" ${line} stroke-width="7"/>
    <circle cx="272" cy="232" r="20" fill="${css('leaf')}" ${line} stroke-width="6"/>
    <circle cx="312" cy="224" r="16" fill="${css('leafLight')}" ${line} stroke-width="6"/>
    <circle cx="292" cy="206" r="17" fill="${css('leaf')}" ${line} stroke-width="6"/>
  </svg>`
}

/**
 * A food picture as a DOM element, sized in CSS px.
 *
 * Size is set inline three times for exactly the reason the HUD crew documents
 * on `iconEl`: an `<svg>` with only a viewBox has no intrinsic size, and if a
 * stylesheet has not landed yet it claims the whole containing block. This
 * cannot depend on CSS arriving.
 */
export function picEl(pic: FoodPic, pen: PicPen, sizePx: number): HTMLSpanElement {
  const span = document.createElement('span')
  span.className = 'mt-ico mt-food'
  span.style.display = 'block'
  span.style.flex = 'none'
  span.style.width = `${sizePx}px`
  span.style.height = `${sizePx}px`
  span.innerHTML = foodSvg(pic, pen)
  const svg = span.firstElementChild
  if (svg !== null) {
    svg.setAttribute('width', '100%')
    svg.setAttribute('height', '100%')
  }
  return span
}

/** Wrap arbitrary SVG body in the same shell — used by the zoo's animal art. */
export function svgEl(body: string, sizePx: number, className = 'mt-ico'): HTMLSpanElement {
  const span = document.createElement('span')
  span.className = className
  span.style.display = 'block'
  span.style.flex = 'none'
  span.style.width = `${sizePx}px`
  span.style.height = `${sizePx}px`
  span.innerHTML = `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true" width="100%" height="100%">${body}</svg>`
  return span
}

export { OUT as PIC_OUT, DET as PIC_DET, INK as PIC_INK }
